import { useState, useEffect, useCallback } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

const SESSIONS_PER_PAGE = 20;

/**
 * Hook to fetch sessions for a project with pagination
 * Includes delete functions for individual and bulk deletion
 */
export function useProjectSessions(userId, projectId) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchSessions = useCallback(async (append = false) => {
    if (!userId || !projectId) {
      setLoading(false);
      return;
    }

    try {
      if (!append) {
        setLoading(true);
      }
      setError(null);

      const db = firebase.firestore();
      const sessionsRef = db
        .collection('user')
        .doc(userId)
        .collection('url')
        .doc(projectId)
        .collection('testingSessions');

      let query = sessionsRef.orderBy('startedAt', 'desc').limit(SESSIONS_PER_PAGE + 1);

      // If appending, start after the last session
      if (append && sessions.length > 0) {
        const lastSession = sessions[sessions.length - 1];
        query = sessionsRef
          .orderBy('startedAt', 'desc')
          .startAfter(lastSession.startedAt)
          .limit(SESSIONS_PER_PAGE + 1);
      }

      const snapshot = await query.get();
      const fetchedSessions = snapshot.docs.slice(0, SESSIONS_PER_PAGE).map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startedAt: data.startedAt?.toDate?.() || data.startedAt,
          completedAt: data.completedAt?.toDate?.() || data.completedAt
        };
      });

      // Check if there are more sessions
      setHasMore(snapshot.docs.length > SESSIONS_PER_PAGE);

      if (append) {
        setSessions((prev) => [...prev, ...fetchedSessions]);
      } else {
        setSessions(fetchedSessions);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId, sessions]);

  // Delete a single session
  const deleteSession = useCallback(async (sessionId) => {
    if (!userId || !projectId || !sessionId) return false;

    try {
      setDeleting(true);
      const db = firebase.firestore();

      await db
        .collection('user')
        .doc(userId)
        .collection('url')
        .doc(projectId)
        .collection('testingSessions')
        .doc(sessionId)
        .delete();

      // Remove from local state
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      return true;
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.message);
      return false;
    } finally {
      setDeleting(false);
    }
  }, [userId, projectId]);

  // Delete multiple sessions (bulk delete)
  const deleteSessions = useCallback(async (sessionIds) => {
    if (!userId || !projectId || !sessionIds?.length) return false;

    try {
      setDeleting(true);
      const db = firebase.firestore();
      const batch = db.batch();

      sessionIds.forEach((sessionId) => {
        const ref = db
          .collection('user')
          .doc(userId)
          .collection('url')
          .doc(projectId)
          .collection('testingSessions')
          .doc(sessionId);
        batch.delete(ref);
      });

      await batch.commit();

      // Remove from local state
      setSessions((prev) => prev.filter((s) => !sessionIds.includes(s.id)));
      return true;
    } catch (err) {
      console.error('Error deleting sessions:', err);
      setError(err.message);
      return false;
    } finally {
      setDeleting(false);
    }
  }, [userId, projectId]);

  // Load more sessions
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchSessions(true);
    }
  }, [loading, hasMore, fetchSessions]);

  useEffect(() => {
    fetchSessions();
  }, [userId, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    sessions,
    loading,
    error,
    hasMore,
    deleting,
    loadMore,
    deleteSession,
    deleteSessions,
    refetch: () => fetchSessions(false)
  };
}

export default useProjectSessions;
